import React, { useEffect, useState } from "react"

import {
  Avatar,
  Box,
  Button,
  Popover,
  Stack,
  Typography,
  useTheme,
} from "@mui/material"
import {
  useMutationTeacherAdmin,
  useMutationTeacherManagementDelete,
} from "api/reactQuery/mutations/adminTeacher"
import {
  useMutationTeacherManagementAccept,
  useMutationTeacherManagementReject,
} from "api/reactQuery/mutations/teacherManagement"
import {
  useQueryTeacherManagementPending,
  useQueryTeachersManagement,
} from "api/reactQuery/queries/teacherAdmin"
import { TeachersManagement } from "api/reactQuery/queries/teacherAdmin.types"
import { ReactComponent as IconCheck } from "assets/common/svg/check.svg"
import { ReactComponent as IconCross } from "assets/common/svg/cross.svg"
import { ReactComponent as IconDots } from "assets/common/svg/threeDots.svg"
import LoadingIndicator from "components/common/loadingIndicator"
import BasicTable from "components/common/table/basicTable"
import { useIsBreakpointUp } from "hooks/breakpoint"
import { useDialog } from "hooks/dialog"
import { usePopover } from "hooks/popover"
import { useCustomSnackbar } from "hooks/snackbar"
import { useTranslation } from "react-i18next"
import { useInView } from "react-intersection-observer"
import { useQueryClient } from "react-query"
import { useNavigate } from "react-router-dom"
import { RoutePath } from "routes/Route.types"
import { unstyledButtonSx } from "styles/common/button"
import { visuallyHiddenSx } from "styles/common/typography"
import { getErrorMessageFromTab } from "utils/api"

import Dialog from "../../../common/dialog/dialog"
import SearchbarForm from "../../../form/searchbarForm"
import TeachersMobileMenu from "./TeachersMobileMenu"
import TeachersTabs from "./TeachersTabs"

export type TabNameTypes = ("active" | "pending") | null

const Teachers = () => {
  const { t } = useTranslation()
  const { showSnackbar } = useCustomSnackbar()
  const theme = useTheme()
  const navigate = useNavigate()
  const [search, setSearch] = useState("")
  const [tabName, setTabName] = useState<TabNameTypes>("active")
  const [teacherProps, setTeacherProps] = useState<TeachersManagement | null>(
    null
  )
  const queryClient = useQueryClient()
  const { ref, inView } = useInView()
  const isTablet = useIsBreakpointUp("tablet")

  const handleAlignment = (
    event: React.MouseEvent<HTMLElement>,
    newAlignment: TabNameTypes
  ) => {
    if (newAlignment !== null) {
      setTabName(newAlignment)
    }
  }

  const handleMobileTabChange = (tab: "active" | "pending") => {
    setTabName(tab)
  }

  const { data, hasNextPage, fetchNextPage, isLoading } =
    useQueryTeachersManagement({
      skip: 0,
      limit: 20,
      filter: search.trim().length > 1 ? search.trim() : undefined,
      options: {
        onError: (err) => {
          showSnackbar({
            title: getErrorMessageFromTab(err),
            variant: "error",
          })
        },
      },
    })

  useEffect(() => {
    inView && fetchNextPage()
  }, [inView])

  const { handleClick, popoverAnchorEl, handleClose } =
    usePopover<HTMLButtonElement>()

  const { handleClose: handleCloseDialog, handleOpen, isOpen } = useDialog()

  const { mutate } = useMutationTeacherAdmin({
    options: {
      onSuccess: () => {
        showSnackbar({
          title: "Admin rights have been successfully changed",
        })
        queryClient.invalidateQueries(["teachersManagement"])
        handleClose()
      },
    },
  })

  const {
    data: dataPending,
    hasNextPage: hasNextPagePending,
    fetchNextPage: _fetchNextPagePending,
    isLoading: isLoadingPending,
  } = useQueryTeacherManagementPending({
    skip: 0,
    limit: 20,
    filter: search.trim().length > 1 ? search.trim() : undefined,
    options: {
      onError: (err) => {
        showSnackbar({
          title: getErrorMessageFromTab(err),
          variant: "error",
        })
      },
    },
  })

  const { mutate: deleteTeacher } = useMutationTeacherManagementDelete({
    options: {
      onSuccess: () => {
        queryClient.invalidateQueries(["teachersManagement"])
        handleCloseDialog()
        showSnackbar({
          title: "Teacher has been deleted successfully",
        })
      },
      onError: (err) => {
        showSnackbar({
          title: getErrorMessageFromTab(err),
          variant: "error",
        })
      },
    },
  })

  const { mutate: acceptTeacher, isLoading: isLoadingAcceptTeacher } =
    useMutationTeacherManagementAccept({
      options: {
        onSuccess: () => {
          showSnackbar({
            title: "Teacher has been successfully added",
          })
          queryClient.invalidateQueries(["teachersManagementPending"])
        },
        onError: (err) => {
          showSnackbar({
            title: getErrorMessageFromTab(err),
            variant: "error",
          })
        },
      },
    })

  const { mutate: rejectTeacher, isLoading: isLoadingRejectTeacher } =
    useMutationTeacherManagementReject({
      options: {
        onSuccess: () => {
          showSnackbar({
            title: "Teacher has been successfully rejected",
          })
          queryClient.invalidateQueries(["teachersManagementPending"])
        },
        onError: (err) => {
          showSnackbar({
            title: getErrorMessageFromTab(err),
            variant: "error",
          })
        },
      },
    })

  const hasNoResults =
    !isLoading && !data?.pages?.flatMap((page) => page.data)?.length
  const hasNoResultsPending =
    !isLoadingPending &&
    !dataPending?.pages?.flatMap((page) => page.data)?.length

  const [liveMessage, setLiveMessage] = useState("")

  useEffect(() => {
    const shouldAnnounce =
      (tabName === "active" && hasNoResults) ||
      (tabName === "pending" && hasNoResultsPending)

    setLiveMessage("")

    if (!shouldAnnounce) return

    const timeoutId = setTimeout(() => {
      setLiveMessage(t("adminDashboard.noResultsMatchingFilters"))
    }, 100)

    return () => clearTimeout(timeoutId)
  }, [tabName, hasNoResults, hasNoResultsPending, t])

  return (
    <Stack sx={{ position: "relative" }}>
      {isTablet && (
        <Stack sx={{ position: "absolute", top: -20, right: 30, zIndex: 10 }}>
          <Button
            onClick={() => navigate(RoutePath.ADMIN_ADD_TEACHER)}
          >
            {t("adminDashboard.addTeacher")}
          </Button>
        </Stack>
      )}
      <Typography role="status" sx={visuallyHiddenSx}>
        {liveMessage}
      </Typography>
      {isTablet ? (
        <TeachersTabs tabName={tabName} onTabChange={handleAlignment} />
      ) : (
        <TeachersMobileMenu tabName={tabName} onTabChange={handleMobileTabChange} />
      )}
      {tabName === "active" && (
        <>
          <BasicTable
            wrapperProps={{ borderRadius: "0 8px 8px 8px" }}
            topSlot={
              <Stack p={2}>
                <SearchbarForm
                  placeholder={t("adminDashboard.searchForTeachers")}
                  onSubmit={(data) => setSearch(data.search)}
                />
              </Stack>
            }
            head={
              <BasicTable.Row>
                <BasicTable.Cell sx={{ maxWidth: "100%" }}>
                  <BasicTable.HeadText>
                    {t("adminDashboard.teacher")}
                  </BasicTable.HeadText>
                </BasicTable.Cell>
                <BasicTable.Cell tableCellProps={{ width: "50px" }}>
                  <BasicTable.HeadText>
                    {t("adminDashboard.admin")}
                  </BasicTable.HeadText>
                </BasicTable.Cell>
                <BasicTable.Cell tableCellProps={{ width: "50px" }} />
              </BasicTable.Row>
            }
            body={data?.pages.flatMap((item) =>
              item.data.map((teacher) => (
                <BasicTable.Row key={teacher.id}>
                  <BasicTable.Cell>
                    <Stack
                      flexDirection="row"
                      columnGap={1}
                      alignItems="center"
                    >
                      <Avatar
                        src={teacher.avatar_url || ""}
                        alt={`${teacher.first_name}`}
                      />
                      <Stack>
                        <Typography>
                          {teacher.first_name} {teacher.last_name}
                        </Typography>
                        <Typography fontWeight="500" variant="body2">
                          {teacher.email}
                        </Typography>
                      </Stack>
                    </Stack>
                  </BasicTable.Cell>
                  <BasicTable.Cell sx={{ justifyContent: "center" }}>
                    {teacher.is_school_admin ? (
                      <Stack
                        sx={{
                          backgroundColor: theme.palette.mockup.successLight,
                          px: 1,
                          borderRadius: "25%",
                        }}
                      >
                        <Typography color="mockup.success50">YES</Typography>
                      </Stack>
                    ) : (
                      <Stack
                        sx={{
                          px: 1,
                          borderRadius: "25%",
                          backgroundColor: theme.palette.mockup.neutralWeak100,
                        }}
                      >
                        <Typography color="mockup.neutral10">NO</Typography>
                      </Stack>
                    )}
                  </BasicTable.Cell>
                  <BasicTable.Cell>
                    <Stack
                      component="button"
                      type="button"
                      aria-label={t("adminDashboard.teacherOptions")}
                      onClick={(e) => {
                        handleClick(e)
                        setTeacherProps({ ...teacher })
                      }}
                      sx={{
                        ...unstyledButtonSx,
                        borderRadius: "50%",
                        width: "30px",
                        height: "30px",
                        border: `1px solid ${theme.palette.primary.main}`,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <IconDots
                        style={{
                          cursor: "pointer",
                          width: "20px",
                          height: "20px",
                        }}
                      />
                    </Stack>
                    <Popover
                      PaperProps={{
                        sx: {
                          boxShadow: "none",
                          borderColor: theme.palette.mockup.neutral40,
                        },
                      }}
                      open={!!popoverAnchorEl}
                      anchorEl={popoverAnchorEl}
                      onClose={handleClose}
                      anchorOrigin={{
                        vertical: "center",
                        horizontal: "left",
                      }}
                      transformOrigin={{
                        vertical: "top",
                        horizontal: "right",
                      }}
                    >
                      <Stack rowGap={2} p={2}>
                        <Typography
                          component="button"
                          type="button"
                          onClick={() => {
                            if (teacherProps) {
                              mutate({
                                is_school_admin: !teacherProps.is_school_admin,
                                teacher_id: teacherProps.id,
                              })
                            }
                          }}
                          variant="body2"
                          fontWeight="500"
                          sx={{
                            ...unstyledButtonSx,
                            display: "block",
                            width: "100%",
                            textAlign: "left",
                            cursor: "pointer",
                            transition: "opacity 0.5s",
                            "&:hover": {
                              opacity: 0.7,
                            },
                          }}
                        >
                          {`${
                            teacherProps?.is_school_admin ? "Remove" : "Make"
                          } admin `}
                        </Typography>
                        <Typography
                          component="button"
                          type="button"
                          onClick={handleOpen}
                          variant="body2"
                          fontWeight="500"
                          sx={{
                            ...unstyledButtonSx,
                            display: "block",
                            width: "100%",
                            textAlign: "left",
                            cursor: "pointer",
                            transition: "opacity 0.5s",
                            "&:hover": {
                              opacity: 0.7,
                            },
                          }}
                        >
                          Delete teacher
                        </Typography>
                      </Stack>
                    </Popover>
                  </BasicTable.Cell>
                </BasicTable.Row>
              ))
            )}
            bottomSlot={
              <>
                {isLoading && (
                  <Box
                    component="span"
                    sx={{ display: "block", textAlign: "center", marginTop: 2 }}
                  >
                    <LoadingIndicator />
                  </Box>
                )}
                <Typography m={hasNoResults ? 2 : 0}>
                  {hasNoResults && t("adminDashboard.noResultsMatchingFilters")}
                </Typography>
                {hasNextPage && <div ref={ref} />}
              </>
            }
          />
          <Dialog
            open={isOpen}
            onClose={handleCloseDialog}
            titleText={`Are you sure you want to delete the teacher ${teacherProps?.first_name}
        ${teacherProps?.last_name}?`}
            onActionButtonClick={() => {
              if (teacherProps) deleteTeacher({ teacher_id: teacherProps.id })
            }}
          />
        </>
      )}
      {tabName === "pending" && (
        <Stack
          sx={{
            border: `1px solid ${theme.palette.mockup.primary95}`,
            borderTop: "none",
            borderBottom: "none",
          }}
        >
          <Stack
            p={2}
            sx={{
              backgroundColor: theme.palette.common.white,
              border: `1px solid ${theme.palette.mockup.primary95}`,
              borderRadius: "0 8px 0 0",
              borderBottom: "none",
            }}
          >
            <SearchbarForm
              placeholder={t("adminDashboard.searchForTeachers")}
              onSubmit={(data) => setSearch(data.search)}
            />
          </Stack>
          {dataPending?.pages.flatMap((item) =>
            item.data.map((teacher, index) => (
              <Stack
                key={teacher.id}
                justifyContent="space-between"
                flexDirection="row"
                alignItems="center"
                py={1}
                px={2}
                sx={{
                  backgroundColor:
                    index % 2 === 0
                      ? theme.palette.common.white
                      : theme.palette.mockup.primary99,
                }}
              >
                <Stack
                  flexDirection="row"
                  columnGap={1}
                  alignItems="center"
                  py={1}
                  sx={{ flex: 1, minWidth: 0 }}
                >
                  <Avatar
                    src={teacher.avatar_url || ""}
                    alt={`${teacher.first_name}`}
                  />
                  <Stack sx={{ minWidth: 0 }}>
                    <Typography noWrap>
                      {teacher.first_name} {teacher.last_name}
                    </Typography>
                    <Typography noWrap fontWeight="500" variant="body2">
                      {teacher.email}
                    </Typography>
                  </Stack>
                </Stack>
                {isTablet ? (
                  <Stack
                    flexDirection="row"
                    sx={{ maxHeight: "48px" }}
                    alignItems="center"
                  >
                    <Button
                      disabled={isLoadingRejectTeacher}
                      onClick={() => rejectTeacher({ teacherId: teacher.id })}
                      variant="text"
                    >
                      Reject
                    </Button>
                    <Button
                      disabled={isLoadingAcceptTeacher}
                      variant="outlined"
                      onClick={() => acceptTeacher({ teacherId: teacher.id })}
                    >
                      Accept
                    </Button>
                  </Stack>
                ) : (
                  <Stack flexDirection="row" columnGap={1}>
                    <Stack
                      component="button"
                      type="button"
                      disabled={isLoadingRejectTeacher}
                      aria-label={t("adminDashboard.rejectTeacher")}
                      onClick={() => rejectTeacher({ teacherId: teacher.id })}
                      sx={{
                        ...unstyledButtonSx,
                        height: "32px",
                        width: "32px",
                        color: theme.palette.mockup.primary60,
                        borderRadius: "50%",
                        border: `1px solid ${theme.palette.primary.main}`,
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        opacity: isLoadingRejectTeacher ? 0.4 : 1,
                        cursor: isLoadingRejectTeacher ? "default" : "pointer",
                      }}
                    >
                      <IconCross style={{ width: "100%", height: "100%" }} />
                    </Stack>
                    <Stack
                      component="button"
                      type="button"
                      disabled={isLoadingAcceptTeacher}
                      aria-label={t("adminDashboard.acceptTeacher")}
                      onClick={() => acceptTeacher({ teacherId: teacher.id })}
                      sx={{
                        ...unstyledButtonSx,
                        height: "32px",
                        width: "32px",
                        color: theme.palette.mockup.primary60,
                        borderRadius: "50%",
                        border: `1px solid ${theme.palette.primary.main}`,
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        opacity: isLoadingAcceptTeacher ? 0.4 : 1,
                        cursor: isLoadingAcceptTeacher ? "default" : "pointer",
                      }}
                    >
                      <IconCheck style={{ width: "100%", height: "100%" }} />
                    </Stack>
                  </Stack>
                )}
              </Stack>
            ))
          )}
          <Stack sx={{ backgroundColor: theme.palette.common.white }}>
            {isLoadingPending && (
              <Box
                component="span"
                sx={{ display: "block", textAlign: "center", marginTop: 2 }}
              >
                <LoadingIndicator />
              </Box>
            )}
            <Typography m={hasNoResultsPending ? 2 : 0}>
              {hasNoResultsPending &&
                t("adminDashboard.noResultsMatchingFilters")}
            </Typography>
            {hasNextPagePending && <div ref={ref} />}
          </Stack>
        </Stack>
      )}
    </Stack>
  )
}
export default Teachers
